import Logger from "@/utility/logger";
import { TODO_TEXT_COLOR } from "@/utility/captionTodoColor";
import {
  TASK_ITEM_MARKER_CLOSE,
  TASK_ITEM_MARKER_OPEN_PATTERN,
  parseTaskItemOpenMarker,
  taskItemCommentText,
} from "@/utility/taskItem";
import { hasJenlyComment, loadExistingComments } from "./wordExistingComments";

/**
 * Resolves the task-item spans in `searchRange`: each message is styled like a TODO and
 * gets one severity comment over exactly its own text, then its markers are deleted.
 *
 * The span ends are delimited by unique markers, so no sentence-end guessing is needed.
 * Open and close markers are paired by document order; when their counts differ (a marker
 * was damaged) the markers are still removed, but nothing is styled or commented.
 *
 * A message that already carries a Jenly comment (a run interrupted after commenting but
 * before the markers were removed) is styled again but not commented a second time.
 */
export const applyTaskItems = async (
  context: Word.RequestContext,
  searchRange: Word.Range | Word.Body,
  shouldHighlight?: boolean,
  shouldGenerateComments?: boolean
) => {
  const opens = searchRange.search(TASK_ITEM_MARKER_OPEN_PATTERN, { matchWildcards: true, matchCase: true });
  const closes = searchRange.search(TASK_ITEM_MARKER_CLOSE, { matchWildcards: false, matchCase: true });
  opens.load("items/text");
  closes.load("items");
  await context.sync();

  const openItems = opens.items || [];
  const closeItems = closes.items || [];
  if (openItems.length === 0 && closeItems.length === 0) return;

  if (openItems.length === closeItems.length) {
    const messages = openItems.map((open, index) =>
      open.getRange(Word.RangeLocation.after).expandTo(closeItems[index].getRange(Word.RangeLocation.start))
    );
    const existing = shouldGenerateComments ? await loadExistingComments(context, messages) : [];
    messages.forEach((message, index) => {
      if (shouldHighlight) {
        message.font.color = TODO_TEXT_COLOR;
        message.font.bold = true;
        message.font.highlightColor = null;
      }
      const code = parseTaskItemOpenMarker(openItems[index].text || "");
      if (shouldGenerateComments && code !== null && !hasJenlyComment(existing[index])) {
        message.insertComment(taskItemCommentText(code));
      }
    });
    await context.sync();
  } else {
    Logger.warn("wordTaskItems.ts: Task item markers are unpaired; removing them without styling.", {
      open: openItems.length,
      close: closeItems.length,
    });
  }

  openItems.forEach((open) => open.delete());
  closeItems.forEach((close) => close.delete());
  await context.sync();
};
